import type {
  QuickSearchHierarchyResult,
  QuickSearchProductResult,
  QuickSearchResponse,
  QuickSearchResult,
} from '@/types/searchTypes'

export type QuickSearchCategoryView = keyof QuickSearchResponse['categories']
export type QuickSearchView = 'all' | QuickSearchCategoryView

export function isQuickSearchView(value: string | null): value is QuickSearchView {
  return value === 'all'
    || value === 'subdepartments'
    || value === 'families'
    || value === 'supermodels'
    || value === 'models'
}

function buildDepartmentPath(universeId: number, departmentId: number, season: string) {
  return `/${season}/universe/${universeId}/department/${departmentId}`
}

function dedupeFamilyIds(results: QuickSearchResult[]) {
  return [...new Set(results.flatMap(result => result.routeTarget.familyIds))]
}

function buildFamiliesSearch(familyIds: number[], viewMode?: 'sm' | 'model', code?: string) {
  const searchParams = new URLSearchParams()

  familyIds.forEach(id => searchParams.append('families', id.toString()))

  if (viewMode) {
    searchParams.set('viewMode', viewMode)
  }

  if (code) {
    searchParams.set('product_code', code)
  }

  return searchParams.toString()
}

export function buildQuickSearchResultLocation(result: QuickSearchResult, season: string) {
  const { routeTarget } = result
  const basePath = buildDepartmentPath(
    routeTarget.universeId,
    routeTarget.departmentId,
    season,
  )

  const isProductResult = result.type === 'supermodel' || result.type === 'model'
  const familyPath = result.type === 'subdepartment' ? 'full/families' : 'families'
  const viewMode = isProductResult ? routeTarget.viewMode : undefined
  const code = isProductResult ? result.code : undefined

  return `${basePath}/subdepartment/${routeTarget.subdepartmentId}/${familyPath}?${buildFamiliesSearch(routeTarget.familyIds, viewMode, code)}`
}

function buildSeeAllCategoryLocation(
  results: QuickSearchHierarchyResult[] | QuickSearchProductResult[],
  season: string,
  viewMode?: 'sm' | 'model',
) {
  const first = results[0]
  if (!first) {
    return `/${season}`
  }

  const familyIds = dedupeFamilyIds(results)
  const basePath = buildDepartmentPath(
    first.routeTarget.universeId,
    first.routeTarget.departmentId,
    season,
  )

  return `${basePath}/subdepartments/families?${buildFamiliesSearch(familyIds, viewMode)}`
}

function buildSubdepartmentsSeeAllLocation(results: QuickSearchHierarchyResult[], season: string) {
  const first = results[0]
  if (!first) {
    return `/${season}`
  }

  return buildDepartmentPath(
    first.routeTarget.universeId,
    first.routeTarget.departmentId,
    season,
  )
}

export function buildQuickSearchSeeAllLocation(
  view: QuickSearchCategoryView,
  response: QuickSearchResponse,
  season: string,
) {
  switch (view) {
    case 'subdepartments':
      return buildSubdepartmentsSeeAllLocation(response.categories.subdepartments.results, season)
    case 'families':
      return buildSeeAllCategoryLocation(response.categories.families.results, season)
    case 'supermodels':
      return buildSeeAllCategoryLocation(response.categories.supermodels.results, season, 'sm')
    case 'models':
      return buildSeeAllCategoryLocation(response.categories.models.results, season, 'model')
    default:
      return `/${season}`
  }
}
